// MIDAS V3 — <live-row/> + <src-ref/> grammar helpers (MIDAS-ANSWER-FORMAT-1
// round 5, bot request 2026-10-04, BOT_CAPABILITIES.md §12).
//
// Wire format (server-minted by the bot's web formatter, never LLM text):
//   <live-row/>                          one per table row verified live
//   <src-ref kind="logic"/>              "Logika stroja" source pill
//   <src-ref kind="live" time="HH:MM"/>  "Naživo zo stroja · HH:MM" pill
//
// CLOSED grammar. The bot fullmatches `[[live-row]]`, `[[src:logic]]` and
// `[[src:live:\d{2}:\d{2}]]` before minting, so anything else reaching us is a
// bot bug. Same rule as every other chip family in markdownComponentsV3: a tag
// that fails validation is left VERBATIM (react-markdown 9 renders raw HTML as
// visible text), so the bug stays visible and reportable — never a half chip.
// The FINAL message never parses the `[[…]]` shorthands: the bot mints them
// (or degrades them to plain text while `MIDAS_SRC_CHIPS=off`) before the
// `answer` event, and leaves a malformed one as raw `[[…]]` on purpose.
// The STREAMING surface is different (r5c, bot r5 answer 4): the token deltas
// carry the raw shorthand and the elements arrive only in the final event, so
// mid-stream we expand exactly the mint grammar — `[[live-row]]`,
// `[[src:logic]]`, `[[src:live:HH:MM]]` (valid 24 h time), case-sensitive, no
// whitespace — into the same void tags (expandStreamSrcShorthand below).
// Nothing wider: a frontend grammar wider than the mint grammar is the skew
// SEC-REQ-CT-1 warns about (see telqGrammar.ts).
//
// Pure — no React, no DOM. markdownComponentsV3 owns the sentinel plumbing;
// this file owns the validation rules, the sources-row ordering, the table
// lane detection and the streaming hold-back for these two VOID tags.

export type SrcRefKind = "logic" | "live";

export interface SrcRefPayload {
  kind: SrcRefKind;
  /** `HH:MM` 24 h — present for kind "live" only. */
  time?: string;
}

/** Attributes `<src-ref>` may carry. Anything else rejects the tag. */
export const SRC_REF_ATTRS = ["kind", "time"];

// 24 h clock, zero-padded, exactly `HH:MM` (the bot's `\d{2}:\d{2}` with the
// range check the regex alone does not give: 24:00 / 00:60 are not times).
const LIVE_READ_TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export const isLiveReadTime = (value: string | undefined): value is string =>
  typeof value === "string" && LIVE_READ_TIME_RE.test(value);

// One `name=value` pair (double-, single- or un-quoted value). Used only to
// prove the attribute blob holds nothing BUT known pairs.
const ATTR_PAIR_RE = /([^\s=/>]+)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+)/g;

/**
 * True when `attrs` (a tag's raw attribute blob) contains only `name=value`
 * pairs whose names are in `allowed`, each at most once. A boolean attribute,
 * stray text, an unknown name or a duplicate (`kind="logic" kind="live"` —
 * readAttr would silently take the first) all return false.
 */
export const isClosedAttrBlob = (attrs: string, allowed: string[]): boolean => {
  const seen = new Set<string>();
  let ok = true;
  const rest = (attrs || "").replace(ATTR_PAIR_RE, (_whole, rawName: string) => {
    const name = rawName.toLowerCase();
    if (!allowed.includes(name) || seen.has(name)) ok = false;
    seen.add(name);
    return " ";
  });
  return ok && rest.trim() === "";
};

/**
 * Validate decoded `<src-ref>` attribute values against the closed grammar.
 *   kind="logic"            → no `time` allowed
 *   kind="live" time=HH:MM  → `time` required and must be a real 24 h time
 * Anything else (unknown / missing kind, bad or missing time on live, time on
 * logic) → null, and the caller leaves the tag verbatim.
 */
export const validateSrcRef = (
  kind: string | undefined,
  time: string | undefined
): SrcRefPayload | null => {
  if (kind === "logic") return time === undefined ? { kind: "logic" } : null;
  if (kind === "live") return isLiveReadTime(time) ? { kind: "live", time } : null;
  return null;
};

/**
 * Sources-row order (Paper "Variant E": page · sheet · LOGIC · LIVE). The page
 * and sheet cards come first from the artifacts strip; here we only order the
 * src pills — logic before live, wire order kept within a kind — and drop
 * exact duplicates (two identical pills say nothing a single one doesn't).
 */
export const orderSrcRefs = (refs: SrcRefPayload[]): SrcRefPayload[] => {
  const seen = new Set<string>();
  const unique = refs.filter((r) => {
    const key = `${r.kind}|${r.time ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [
    ...unique.filter((r) => r.kind === "logic"),
    ...unique.filter((r) => r.kind === "live"),
  ];
};

/**
 * A line left over after its src-ref chips were hoisted into the sources row:
 * nothing but whitespace, list/quote/emphasis punctuation, separators and an
 * optional "Zdroje:" / "Sources:" label. Such a line is dropped so the prose
 * does not end in a dangling label (the sources row carries its own eyebrow).
 */
const SOURCES_RESIDUE_RE =
  /^[\s>#*_`~\-–—·•|,;:]*(?:zdroje|zdroj|sources?)?[\s#*_`~\-–—·•|,;:]*$/i;

export const isSourcesResidueLine = (line: string): boolean =>
  SOURCES_RESIDUE_RE.test(line);

// A line that is ONLY a sources label ("Zdroje:", "**Zdroje:**", "## Sources")
// — dropped too when it sits directly above a chip line that was hoisted.
const SOURCES_LABEL_RE =
  /^[\s>#*_`~\-–—·•|:]*(?:zdroje|zdroj|sources?)[\s#*_`~\-–—·•|:]*$/i;

export const isSourcesLabelLine = (line: string): boolean =>
  SOURCES_LABEL_RE.test(line);

// ─── Table lane detection (empty-slot parity) ────────────────────────
//
// Minimal hast shape — react-markdown 9 hands every custom renderer its hast
// `node` (passNode: true), and the SAME node objects appear as descendants of
// the table node, so a WeakSet of cell nodes computed at the table can be
// queried by each cell renderer.
interface HastLike {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastLike[];
}

const textOf = (n: HastLike): string =>
  n.type === "text" ? n.value || "" : (n.children || []).map(textOf).join("");

const isEl = (n: HastLike, ...tags: string[]): boolean =>
  n.type === "element" && !!n.tagName && tags.includes(n.tagName);

/**
 * Find the body cells that sit in a "live pulse" column but carry no marker.
 * A column is a live lane when at least one body cell in it contains
 * `marker` (the LIVEROW sentinel). Every EMPTY body cell in such a column gets
 * the same-width empty slot (Paper "no live" dash), so the pulse icons and
 * the dashes form one vertical lane. Returns null when the table has no live
 * lane at all (a machine without a live feed → plain empty cells, no dashes).
 */
export const findEmptyLiveLaneCells = (
  table: unknown,
  marker: string
): WeakSet<object> | null => {
  const t = table as HastLike | undefined;
  if (!t || !Array.isArray(t.children)) return null;

  const rows: HastLike[][] = [];
  t.children.forEach((section) => {
    if (isEl(section, "thead")) return; // the header row is never a pulse slot
    const trs = isEl(section, "tr")
      ? [section]
      : (section.children || []).filter((c) => isEl(c, "tr"));
    trs.forEach((tr) =>
      rows.push((tr.children || []).filter((c) => isEl(c, "td", "th")))
    );
  });

  const liveCols = new Set<number>();
  rows.forEach((cells) =>
    cells.forEach((cell, i) => {
      if (textOf(cell).includes(marker)) liveCols.add(i);
    })
  );
  if (liveCols.size === 0) return null;

  const empty = new WeakSet<object>();
  rows.forEach((cells) =>
    cells.forEach((cell, i) => {
      if (liveCols.has(i) && textOf(cell).trim() === "") empty.add(cell);
    })
  );
  return empty;
};

// ─── Streaming hold-back for VOID chip tags ──────────────────────────
//
// trimUnclosedRefTagTail (shared/chatBot/streamBotMessage.ts) anchors on a
// closing `</tag>`, which a self-closing tag never has — listing these tags
// there would hold the rest of the stream back forever. A void tag is complete
// once its `>` arrives, so we only withhold (a) a last opener with no `>` yet
// and (b) a tail still spelling an opener (`…<src-r`).
// "cond-info" (r6): the bot stamps it on the final `answer` event only, never
// in deltas — listed for symmetry (same rationale as comp-ref in
// STREAMING_CHIP_TAGS), so a half `<cond-info te` could never flash as text.
// "verified-line" (MIDAS-VERIFIED-LINE-1): final-only too, no shorthand —
// listed for the same symmetry, so a half `<verified-l` is held, never shown.
export const VOID_CHIP_TAGS = ["live-row", "src-ref", "cond-info", "verified-line"];

export const trimUnclosedVoidTagTail = (
  buffer: string,
  tags: string[] = VOID_CHIP_TAGS
): string => {
  if (!buffer || buffer.indexOf("<") === -1) return buffer;
  let cut = buffer.length;
  const lastLt = buffer.lastIndexOf("<");
  tags.forEach((tag) => {
    const opener = "<" + tag;
    const idx = buffer.toLowerCase().lastIndexOf(opener);
    if (idx !== -1) {
      const after = buffer.charAt(idx + opener.length);
      const isRealOpener = after === "" || /[\s/>]/.test(after);
      if (isRealOpener && buffer.indexOf(">", idx) === -1) cut = Math.min(cut, idx);
    }
    const suffix = buffer.slice(lastLt).toLowerCase();
    if (suffix.length <= opener.length && opener.startsWith(suffix)) {
      cut = Math.min(cut, lastLt);
    }
  });
  return buffer.slice(0, cut);
};

// ─── Streaming: the raw `[[…]]` shorthand in the token deltas ────────
//
// Same idea as the `[[list:N]]` / `[[page:N]]` shorthands, which
// rewriteAllRefsToSentinels turns into the SAME chip their final tag gives:
// a complete shorthand renders as the element it will become in the final
// `answer` event (pulse in the table lane, pill on the sources line), so there
// is no raw `[[live-row]]` flash and the Variant E table is already detected
// mid-stream. A half-arrived one is held back, like holdPartialMarkerTail
// holds a half `[Step: …` line. Streaming only — see the header note.

const LIVE_ROW_SHORTHAND_RE = /\[\[live-row\]\]/g;
const SRC_LOGIC_SHORTHAND_RE = /\[\[src:logic\]\]/g;
const SRC_LIVE_SHORTHAND_RE = /\[\[src:live:(\d{2}:\d{2})\]\]/g;

/** Complete, mint-exact shorthand → the void tag it is minted into. A live
 *  read with an impossible time (`25:00`) stays raw, as the bot leaves it. */
export const expandStreamSrcShorthand = (text: string): string => {
  if (!text || text.indexOf("[[") === -1) return text;
  return text
    .replace(LIVE_ROW_SHORTHAND_RE, "<live-row/>")
    .replace(SRC_LOGIC_SHORTHAND_RE, '<src-ref kind="logic"/>')
    .replace(SRC_LIVE_SHORTHAND_RE, (whole, time: string) =>
      isLiveReadTime(time) ? `<src-ref kind="live" time="${time}"/>` : whole
    );
};

// `#` = any digit. A tail that is a STRICT prefix of one of these is held.
const SHORTHAND_TEMPLATES = ["[[live-row]]", "[[src:logic]]", "[[src:live:##:##]]"];

const isStrictShorthandPrefix = (tail: string): boolean =>
  SHORTHAND_TEMPLATES.some(
    (tpl) =>
      tail.length < tpl.length &&
      Array.from(tail).every((ch, i) =>
        tpl[i] === "#" ? ch >= "0" && ch <= "9" : ch === tpl[i]
      )
  );

/**
 * Withhold a trailing half shorthand (`[`, `[[`, `[[live-r`, `[[src:live:19:4`)
 * until it completes. A tail that stops matching (`[[lis…` → a list chip,
 * `[text` → a link) is released on the very next character.
 */
export const holdPartialSrcShorthandTail = (buffer: string): string => {
  if (!buffer) return buffer;
  let start = buffer.lastIndexOf("[");
  if (start === -1) return buffer;
  if (start > 0 && buffer.charAt(start - 1) === "[") start -= 1;
  return isStrictShorthandPrefix(buffer.slice(start)) ? buffer.slice(0, start) : buffer;
};
